import { supabase, handleError, formatDate } from './apiClient'

export async function fetchProperties() {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        availableUnits:property_units(*),
        images:property_images(image_url),
        amenities:property_amenities(amenity),
        nearbyAttractions:property_attractions(attraction),
        videos:property_videos(id, video_url, display_order),
        youtubeLinks:property_youtube_links(id, youtube_url, display_order)
      `)
      .eq('status', 'AVAILABLE')
      .order('created_at', { ascending: false })
    
    if (error) handleError(error, 'fetch properties')
    if (!data) return []
    
    return data.map(prop => ({
      ...prop,
      price: prop.starting_price,
      startingPrice: prop.starting_price,
      shortDescription: prop.short_description,
      detailedDescription: prop.detailed_description,
      mainImage: prop.images?.[0]?.image_url || '',
      images: prop.images?.map(img => img.image_url) || [],
      amenities: prop.amenities?.map(a => a.amenity) || [],
      nearbyAttractions: prop.nearbyAttractions?.map(a => a.attraction) || [],
      videos: prop.videos?.map(v => v.video_url) || [],
      youtubeLinks: prop.youtubeLinks?.map(y => y.youtube_url) || [],
      isFeatured: prop.is_featured,
      createdAt: formatDate(prop.created_at)
    }))
  } catch (error) {
    handleError(error, 'fetchProperties')
  }
}

export async function fetchFeaturedProperties() {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        availableUnits:property_units(*),
        images:property_images(image_url),
        amenities:property_amenities(amenity)
      `)
      .eq('status', 'AVAILABLE')
      .eq('is_featured', true)
      .order('created_at', { ascending: false })
    
    if (error) handleError(error, 'fetch featured properties')
    if (!data) return []
    
    return data.map(prop => ({
      ...prop,
      price: prop.starting_price,
      startingPrice: prop.starting_price,
      shortDescription: prop.short_description,
      detailedDescription: prop.detailed_description,
      mainImage: prop.images?.[0]?.image_url || '',
      images: prop.images?.map(img => img.image_url) || [],
      amenities: prop.amenities?.map(a => a.amenity) || [],
      isFeatured: prop.is_featured,
      createdAt: formatDate(prop.created_at)
    }))
  } catch (error) {
    handleError(error, 'fetchFeaturedProperties')
  }
}

export async function fetchPropertyById(id) {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(`
        *,
        availableUnits:property_units(*),
        images:property_images(image_url),
        amenities:property_amenities(amenity),
        nearbyAttractions:property_attractions(attraction),
        videos:property_videos(id, video_url, display_order),
        youtubeLinks:property_youtube_links(id, youtube_url, display_order)
      `)
      .eq('id', id)
      .single()
    
    if (error) handleError(error, 'fetch property details')
    
    return {
      ...data,
      price: data.starting_price,
      startingPrice: data.starting_price,
      shortDescription: data.short_description,
      detailedDescription: data.detailed_description,
      mainImage: data.images?.[0]?.image_url || '',
      images: data.images?.map(img => img.image_url) || [],
      amenities: data.amenities?.map(a => a.amenity) || [],
      nearbyAttractions: data.nearbyAttractions?.map(a => a.attraction) || [],
      videos: data.videos?.map(v => v.video_url) || [],
      youtubeLinks: data.youtubeLinks?.map(y => y.youtube_url) || [],
      isFeatured: data.is_featured,
      googleMapsLink: data.google_maps_link,
      createdAt: formatDate(data.created_at)
    }
  } catch (error) {
    handleError(error, 'fetchPropertyById')
  }
}

export async function searchProperties(params) {
  try {
    let query = supabase
      .from('properties')
      .select(`
        *,
        availableUnits:property_units(*),
        images:property_images(image_url),
        amenities:property_amenities(amenity)
      `)
      .eq('status', 'AVAILABLE')
    
    if (params.location) {
      query = query.ilike('location', `%${params.location}%`)
    }
    if (params.title) {
      query = query.ilike('title', `%${params.title}%`)
    }
    if (params.type) {
      query = query.eq('type', params.type)
    }
    if (params.minPrice) {
      query = query.gte('starting_price', params.minPrice)
    }
    if (params.maxPrice) {
      query = query.lte('starting_price', params.maxPrice)
    }
    
    const { data, error } = await query.order('created_at', { ascending: false })
    
    if (error) handleError(error, 'search properties')
    
    return data.map(prop => ({
      ...prop,
      price: prop.starting_price,
      images: prop.images?.map(img => img.image_url) || [],
      amenities: prop.amenities?.map(a => a.amenity) || [],
      createdAt: formatDate(prop.created_at)
    }))
  } catch (error) {
    handleError(error, 'searchProperties')
  }
}

export async function createProperty(data) {
  try {
    const { data: property, error: propError } = await supabase
      .from('properties')
      .insert([{
        title: data.title,
        short_description: data.shortDescription,
        detailed_description: data.detailedDescription,
        starting_price: parseFloat(data.startingPrice || data.price),
        location: data.location,
        type: data.type,
        status: data.status || 'AVAILABLE',
        bedrooms: data.bedrooms ? parseInt(data.bedrooms) : null,
        bathrooms: data.bathrooms ? parseInt(data.bathrooms) : null,
        is_featured: data.isFeatured || false,
        google_maps_link: data.googleMapsLink || null
      }])
      .select()
      .single()
    
    if (propError) handleError(propError, 'create property')
    
    const propertyId = property.id
    
    if (data.images && data.images.length > 0) {
      const imageInserts = data.images.map((img, idx) => ({
        property_id: propertyId,
        image_url: img,
        display_order: idx
      }))
      await supabase.from('property_images').insert(imageInserts)
    }
    
    if (data.amenities && data.amenities.length > 0) {
      const amenityInserts = data.amenities.map(amenity => ({
        property_id: propertyId,
        amenity: amenity
      }))
      await supabase.from('property_amenities').insert(amenityInserts)
    }
    
    if (data.nearbyAttractions && data.nearbyAttractions.length > 0) {
      const attractionInserts = data.nearbyAttractions.map(attraction => ({
        property_id: propertyId,
        attraction: attraction
      }))
      await supabase.from('property_attractions').insert(attractionInserts)
    }
    
    if (data.availableUnits && data.availableUnits.length > 0) {
      const unitInserts = data.availableUnits.map(unit => ({
        property_id: propertyId,
        size: parseFloat(unit.size),
        price_per_unit: parseFloat(unit.pricePerUnit),
        available_count: parseInt(unit.availableCount),
        total_count: parseInt(unit.totalCount),
        unit_type: unit.unitType || null
      }))
      await supabase.from('property_units').insert(unitInserts)
    }
    
    if (data.videos && data.videos.length > 0) {
      const videoInserts = data.videos
        .filter(v => v && v.trim())
        .map((video, idx) => ({
          property_id: propertyId,
          video_url: typeof video === 'string' ? video : (video.videoUrl || video.video_url || ''),
          display_order: idx
        }))
        .filter(v => v.video_url)
      
      if (videoInserts.length > 0) {
        await supabase.from('property_videos').insert(videoInserts)
      }
    }
    
    if (data.youtubeLinks && data.youtubeLinks.length > 0) {
      const youtubeInserts = data.youtubeLinks
        .filter(y => y && y.trim())
        .map((link, idx) => ({
          property_id: propertyId,
          youtube_url: typeof link === 'string' ? link : (link.youtubeUrl || link.youtube_url || ''),
          display_order: idx
        }))
        .filter(y => y.youtube_url)
      
      if (youtubeInserts.length > 0) {
        await supabase.from('property_youtube_links').insert(youtubeInserts)
      }
    }
    
    return property
  } catch (error) {
    handleError(error, 'createProperty')
  }
}

export async function updateProperty(id, data) {
  try {
    const { data: property, error } = await supabase
      .from('properties')
      .update({
        title: data.title,
        short_description: data.shortDescription,
        detailed_description: data.detailedDescription,
        starting_price: parseFloat(data.startingPrice || data.price),
        location: data.location,
        type: data.type,
        status: data.status,
        bedrooms: data.bedrooms ? parseInt(data.bedrooms) : null,
        bathrooms: data.bathrooms ? parseInt(data.bathrooms) : null,
        is_featured: data.isFeatured || false,
        google_maps_link: data.googleMapsLink || null
      })
      .eq('id', id)
      .select()
    
    if (error) handleError(error, 'update property')
    
    if (data.images !== undefined) {
      await supabase.from('property_images').delete().eq('property_id', id)
      if (data.images.length > 0) {
        const imageInserts = data.images.map((img, idx) => ({
          property_id: id,
          image_url: img,
          display_order: idx
        }))
        await supabase.from('property_images').insert(imageInserts)
      }
    }
    
    if (data.amenities !== undefined) {
      await supabase.from('property_amenities').delete().eq('property_id', id)
      if (data.amenities.length > 0) {
        const amenityInserts = data.amenities.map(amenity => ({
          property_id: id,
          amenity: amenity
        }))
        await supabase.from('property_amenities').insert(amenityInserts)
      }
    }
    
    if (data.nearbyAttractions !== undefined) {
      await supabase.from('property_attractions').delete().eq('property_id', id)
      if (data.nearbyAttractions.length > 0) {
        const attractionInserts = data.nearbyAttractions.map(attraction => ({
          property_id: id,
          attraction: attraction
        }))
        await supabase.from('property_attractions').insert(attractionInserts)
      }
    }
    
    if (data.availableUnits !== undefined) {
      await supabase.from('property_units').delete().eq('property_id', id)
      if (data.availableUnits.length > 0) {
        const unitInserts = data.availableUnits.map(unit => ({
          property_id: id,
          size: parseFloat(unit.size),
          price_per_unit: parseFloat(unit.pricePerUnit),
          available_count: parseInt(unit.availableCount),
          total_count: parseInt(unit.totalCount),
          unit_type: unit.unitType || null
        }))
        await supabase.from('property_units').insert(unitInserts)
      }
    }
    
    if (data.videos !== undefined) {
      await supabase.from('property_videos').delete().eq('property_id', id)
      const videoInserts = data.videos
        .filter(v => v && (typeof v === 'string' ? v.trim() : true))
        .map((video, idx) => ({
          property_id: id,
          video_url: typeof video === 'string' ? video : (video.videoUrl || video.video_url || ''),
          display_order: idx
        }))
        .filter(v => v.video_url && v.video_url.trim())
      
      if (videoInserts.length > 0) {
        await supabase.from('property_videos').insert(videoInserts)
      }
    }
    
    if (data.youtubeLinks !== undefined) {
      await supabase.from('property_youtube_links').delete().eq('property_id', id)
      const youtubeInserts = data.youtubeLinks
        .filter(y => y && (typeof y === 'string' ? y.trim() : true))
        .map((link, idx) => ({
          property_id: id,
          youtube_url: typeof link === 'string' ? link : (link.youtubeUrl || link.youtube_url || ''),
          display_order: idx
        }))
        .filter(y => y.youtube_url && y.youtube_url.trim())
      
      if (youtubeInserts.length > 0) {
        await supabase.from('property_youtube_links').insert(youtubeInserts)
      }
    }
    
    return property
  } catch (error) {
    handleError(error, 'updateProperty')
  }
}

export async function deleteProperty(id) {
  try {
    const { error } = await supabase
      .from('properties')
      .delete()
      .eq('id', id)
    
    if (error) handleError(error, 'delete property')
  } catch (error) {
    handleError(error, 'deleteProperty')
  }
}

export async function toggleFeaturedStatus(id) {
  try {
    const { data: current } = await supabase
      .from('properties')
      .select('is_featured')
      .eq('id', id)
      .single()
    
    const { data, error } = await supabase
      .from('properties')
      .update({ is_featured: !current.is_featured })
      .eq('id', id)
      .select()
    
    if (error) handleError(error, 'toggle featured status')
    return data
  } catch (error) {
    handleError(error, 'toggleFeaturedStatus')
  }
}

export async function fetchPropertyVideos(propertyId) {
  try {
    const { data, error } = await supabase
      .from('property_videos')
      .select('*')
      .eq('property_id', propertyId)
      .order('display_order', { ascending: true })
    
    if (error) handleError(error, 'fetch property videos')
    return data || []
  } catch (error) {
    handleError(error, 'fetchPropertyVideos')
  }
}

export async function addPropertyVideo(propertyId, videoUrl, displayOrder = 0) {
  try {
    const { data, error } = await supabase
      .from('property_videos')
      .insert([{
        property_id: propertyId,
        video_url: videoUrl,
        display_order: displayOrder
      }])
      .select()
      .single()
    
    if (error) handleError(error, 'add property video')
    return data
  } catch (error) {
    handleError(error, 'addPropertyVideo')
  }
}

export async function updatePropertyVideo(videoId, videoUrl, displayOrder) {
  try {
    const { data, error } = await supabase
      .from('property_videos')
      .update({
        video_url: videoUrl,
        display_order: displayOrder
      })
      .eq('id', videoId)
      .select()
      .single()
    
    if (error) handleError(error, 'update property video')
    return data
  } catch (error) {
    handleError(error, 'updatePropertyVideo')
  }
}

export async function deletePropertyVideo(videoId) {
  try {
    const { error } = await supabase
      .from('property_videos')
      .delete()
      .eq('id', videoId)
    
    if (error) handleError(error, 'delete property video')
  } catch (error) {
    handleError(error, 'deletePropertyVideo')
  }
}

export async function fetchPropertyYoutubeLinks(propertyId) {
  try {
    const { data, error } = await supabase
      .from('property_youtube_links')
      .select('*')
      .eq('property_id', propertyId)
      .order('display_order', { ascending: true })
    
    if (error) handleError(error, 'fetch property youtube links')
    return data || []
  } catch (error) {
    handleError(error, 'fetchPropertyYoutubeLinks')
  }
}

export async function addPropertyYoutubeLink(propertyId, youtubeUrl, displayOrder = 0) {
  try {
    const { data, error } = await supabase
      .from('property_youtube_links')
      .insert([{
        property_id: propertyId,
        youtube_url: youtubeUrl,
        display_order: displayOrder
      }])
      .select()
      .single()
    
    if (error) handleError(error, 'add property youtube link')
    return data
  } catch (error) {
    handleError(error, 'addPropertyYoutubeLink')
  }
}

export async function updatePropertyYoutubeLink(youtubeLinkId, youtubeUrl, displayOrder) {
  try {
    const { data, error } = await supabase
      .from('property_youtube_links')
      .update({
        youtube_url: youtubeUrl,
        display_order: displayOrder
      })
      .eq('id', youtubeLinkId)
      .select()
      .single()
    
    if (error) handleError(error, 'update property youtube link')
    return data
  } catch (error) {
    handleError(error, 'updatePropertyYoutubeLink')
  }
}

export async function deletePropertyYoutubeLink(youtubeLinkId) {
  try {
    const { error } = await supabase
      .from('property_youtube_links')
      .delete()
      .eq('id', youtubeLinkId)
    
    if (error) handleError(error, 'delete property youtube link')
  } catch (error) {
    handleError(error, 'deletePropertyYoutubeLink')
  }
}
